import { useCallback, useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

/**
 * First-run onboarding tour — #10803 AC5, frontend-ui-system.md § In-app help system.
 *
 * Shared and portal-agnostic (AC1): each portal passes its own steps, because the two
 * have different audiences and different features visible. Built on Modal, so it
 * inherits the focus trap, focus restore, Escape handling and `role="dialog"` rather
 * than reimplementing them (AC6).
 *
 * Deliberately a sequence of dialogs, NOT spotlight callouts anchored to elements on
 * the page. An anchored tour has to know that each target exists and is visible, and it
 * breaks silently — into an arrow pointing at nothing — whenever a screen is
 * rearranged, gated behind a role, or collapsed at a narrow viewport. The client portal
 * is heavily mobile-used (AC6), which is exactly where anchoring fails most. A dialog
 * sequence renders the same at all four mandatory viewports and cannot point at a
 * control that is not there.
 */

export interface TourStep {
  title: string;
  /** One short paragraph. Kept to plain text — no markup to sanitise. */
  body: string;
}

export interface OnboardingTourProps {
  steps: TourStep[];
  /**
   * localStorage key recording that this tour has been completed or skipped. Namespace
   * it per portal (e.g. `babomrs.tour.staff.v1`) so the two portals are independent and
   * a reworded tour can be re-shown by bumping the version.
   */
  storageKey: string;
  /** Show automatically on first visit. Off for an explicit "replay the tour" trigger. */
  autoStart?: boolean;
  /** Controlled open — when supplied, the tour shows regardless of the stored flag. */
  open?: boolean;
  onClose?: () => void;
}

/**
 * localStorage throws outright in some privacy modes rather than returning null, so
 * every access is guarded. A tour that cannot remember it has run is a small annoyance;
 * a tour that takes down the whole shell on mount is not.
 */
function hasSeen(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'seen';
  } catch {
    // Treat unreadable storage as "already seen" — showing the tour on every single
    // page load would be worse than never showing it.
    return true;
  }
}

function markSeen(key: string): void {
  try {
    window.localStorage.setItem(key, 'seen');
  } catch {
    // Nothing to do — the tour simply shows again next session.
  }
}

export function OnboardingTour({
  steps,
  storageKey,
  autoStart = true,
  open,
  onClose,
}: OnboardingTourProps) {
  const controlled = open !== undefined;
  const [selfOpen, setSelfOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (controlled || !autoStart || steps.length === 0) return;
    if (!hasSeen(storageKey)) setSelfOpen(true);
  }, [controlled, autoStart, storageKey, steps.length]);

  // A controlled re-open starts from the beginning rather than wherever the reader
  // left off last time — "replay the tour" means replay it.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const isOpen = controlled ? !!open : selfOpen;

  const finish = useCallback(() => {
    markSeen(storageKey);
    setSelfOpen(false);
    setStep(0);
    onClose?.();
  }, [storageKey, onClose]);

  if (steps.length === 0 || !isOpen) return null;

  const current = steps[Math.min(step, steps.length - 1)];
  const isLast = step >= steps.length - 1;

  return (
    <Modal
      open={isOpen}
      onClose={finish}
      title={current.title}
      size="sm"
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          <Button variant="ghost" onClick={finish}>
            {isLast ? 'Close' : 'Skip tour'}
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="secondary" onClick={() => setStep((s) => Math.max(0, s - 1))}>
                Back
              </Button>
            )}
            <Button onClick={() => (isLast ? finish() : setStep((s) => s + 1))}>
              {isLast ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {/* Announced so a screen-reader user knows where they are in the sequence. */}
        <p aria-live="polite" className="text-xs font-semibold uppercase tracking-wide text-[var(--text3)]">
          Step {step + 1} of {steps.length}
        </p>
        <p className="text-[var(--text2)]">{current.body}</p>
      </div>
    </Modal>
  );
}
